import { Button } from "taskguard-ui/button";
import { EmptyState } from "taskguard-ui/empty-state";
import { Field } from "taskguard-ui/field";
import { InfoChip } from "taskguard-ui/info-chip";
import { TextArea, TextInput } from "taskguard-ui/inputs";
import { SectionHeading } from "taskguard-ui/section-heading";
import {
  AppPanel,
  ShareHeader,
  SharePanel,
  StackForm,
  VaultActions,
  VaultCardHeader,
  VaultCardSurface,
  VaultGrid,
  VaultListPanel,
  VaultMeta,
  VaultStack,
  VaultsLayout,
} from "taskguard-ui/surfaces";
import { ErrorText, Eyebrow } from "taskguard-ui/text";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatVaultId } from "../../format";
import { useTodoStore, type VaultRecord } from "../../store";

type VaultCardProps = {
  active: boolean;
  onOpenVault: (vaultId: string) => void;
  onShare: (vault: VaultRecord) => void;
  vault: VaultRecord;
};

type ShareState = {
  busy: boolean;
  copied: boolean;
  error: string | null;
  value: string;
  vaultId: string | null;
};

function VaultCard({ active, onOpenVault, onShare, vault }: VaultCardProps) {
  const visibleTodoCount = Object.values(vault.todos).filter((todo) => todo.deletedAt == null).length;

  return (
    <VaultCardSurface active={active}>
      <VaultCardHeader>
        <div>
          <Eyebrow>Vault</Eyebrow>
          <h3>{vault.name}</h3>
        </div>
        <InfoChip tone={active ? "active" : "default"}>{active ? "Active" : "Saved"}</InfoChip>
      </VaultCardHeader>

      <VaultMeta>
        <span>ID {formatVaultId(vault.id)}</span>
        <span>
          {visibleTodoCount} task{visibleTodoCount === 1 ? "" : "s"}
        </span>
      </VaultMeta>

      <VaultActions>
        {!active ? (
          <Button onClick={() => onOpenVault(vault.id)} size="subtle" type="button" variant="ghost">
            Set active
          </Button>
        ) : null}
        <Button onClick={() => onShare(vault)} size="subtle" type="button" variant="ghost">
          Share
        </Button>
      </VaultActions>
    </VaultCardSurface>
  );
}

export function VaultsPage() {
  const navigate = useNavigate();
  const vaultsMap = useTodoStore((state) => state.vaults);
  const activeVaultId = useTodoStore((state) => state.activeVaultId);
  const createVault = useTodoStore((state) => state.createVault);
  const connectToVault = useTodoStore((state) => state.connectToVault);
  const exportVaultConnectionString = useTodoStore((state) => state.exportVaultConnectionString);
  const vaults = useMemo(() => Object.values(vaultsMap).sort((left, right) => left.name.localeCompare(right.name)), [vaultsMap]);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [connectionString, setConnectionString] = useState("");
  const [shareState, setShareState] = useState<ShareState>({
    busy: false,
    copied: false,
    error: null,
    value: "",
    vaultId: null,
  });
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connectBusy, setConnectBusy] = useState(false);

  const handleCreate = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const vaultId = createVault(name, password);

    if (vaultId == null) {
      return;
    }

    setName("");
    setPassword("");
    navigate(`/vaults/${vaultId}`);
  };

  const handleConnect = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (connectBusy) {
      return;
    }

    setConnectBusy(true);
    setConnectError(null);

    try {
      const vaultId = await connectToVault(connectionString);
      setConnectionString("");
      navigate(`/vaults/${vaultId}`);
    } catch (error) {
      setConnectError(error instanceof Error ? error.message : "Unable to connect to that vault.");
    } finally {
      setConnectBusy(false);
    }
  };

  const handleShare = async (vault: VaultRecord) => {
    setShareState({
      busy: true,
      copied: false,
      error: null,
      value: "",
      vaultId: vault.id,
    });

    try {
      const value = await exportVaultConnectionString(vault.id);
      setShareState({
        busy: false,
        copied: false,
        error: null,
        value,
        vaultId: vault.id,
      });
    } catch (error) {
      setShareState({
        busy: false,
        copied: false,
        error: error instanceof Error ? error.message : "Unable to build a connection string.",
        value: "",
        vaultId: vault.id,
      });
    }
  };

  const copyShareString = async () => {
    if (shareState.value.length === 0 || !("clipboard" in navigator)) {
      return;
    }

    await navigator.clipboard.writeText(shareState.value);
    setShareState((current) => ({
      ...current,
      copied: true,
    }));
  };

  const openVault = (vaultId: string) => {
    navigate(`/vaults/${vaultId}`);
  };

  return (
    <VaultsLayout>
      <VaultListPanel>
        <SectionHeading eyebrowText="Vaults" title="Saved locally" />

        {vaults.length === 0 ? (
          <EmptyState title="No vaults yet.">Create one or paste a connection string to bring a shared vault into this browser.</EmptyState>
        ) : (
          <VaultGrid>
            {vaults.map((vault) => (
              <VaultCard active={vault.id === activeVaultId} key={vault.id} onOpenVault={openVault} onShare={handleShare} vault={vault} />
            ))}
          </VaultGrid>
        )}

        {shareState.vaultId != null ? (
          <SharePanel>
            <ShareHeader>
              <div>
                <Eyebrow>Share</Eyebrow>
                <strong>Vault connection string</strong>
              </div>
              <Button onClick={copyShareString} size="subtle" type="button" variant="ghost">
                {shareState.copied ? "Copied" : "Copy"}
              </Button>
            </ShareHeader>
            {shareState.error ? <ErrorText>{shareState.error}</ErrorText> : null}
            <TextArea readOnly value={shareState.busy ? "Preparing..." : shareState.value} />
          </SharePanel>
        ) : null}
      </VaultListPanel>
      <VaultStack>
        <AppPanel>
          <SectionHeading eyebrowText="Create" title="Start a new vault" />

          <StackForm onSubmit={handleCreate}>
            <Field htmlFor="vault-name" label="Vault name">
              <TextInput
                autoComplete="off"
                id="vault-name"
                name="vaultName"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.currentTarget.value)}
                placeholder="Personal, Team, Groceries..."
                value={name}
              />
            </Field>
            <Field htmlFor="vault-password" label="Vault password">
              <TextInput
                autoComplete="new-password"
                id="vault-password"
                name="vaultPassword"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPassword(event.currentTarget.value)}
                placeholder="Vault secret"
                type="password"
                value={password}
              />
            </Field>
            <Button block type="submit">
              Create vault
            </Button>
          </StackForm>
        </AppPanel>

        <AppPanel>
          <SectionHeading eyebrowText="Connect" title="Join an existing vault" />

          <StackForm onSubmit={handleConnect}>
            <Field htmlFor="connection-string" label="Connection string">
              <TextArea
                autoComplete="off"
                id="connection-string"
                name="connectionString"
                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setConnectionString(event.currentTarget.value)}
                placeholder="Paste the encrypted connection string shared by the vault owner."
                spellCheck={false}
                value={connectionString}
              />
            </Field>
            {connectError ? <ErrorText>{connectError}</ErrorText> : null}
            <Button block disabled={connectBusy} type="submit">
              {connectBusy ? "Connecting..." : "Connect vault"}
            </Button>
          </StackForm>
        </AppPanel>
      </VaultStack>
    </VaultsLayout>
  );
}
